import { z } from 'zod'

import type { TicketSection, TicketSort, TicketStatus, TicketView } from '@/client'
import type { TicketCountParams, TicketListParams } from '@/hooks/queries/tickets'
import { addDays, fromIsoDay, isoDay } from '@/lib/dates'
import { PRIMARY_FILTER_KEYS, type PrimaryFilterValue } from '@/lib/filters'
import { NUMBER_OPS, type NumberFilter } from '@/lib/tableFilters'

/**
 * The view state the tickets pages read from the URL.
 *
 * VentaHub kept all of this in sessionStorage, so a filtered queue could not
 * be sent to a colleague and the back button undid nothing. Here every tab —
 * the section (Business or Ads) included — filter, sort, page and column is a
 * search param, optional with a `.catch`, with defaults applied at read time
 * and stripped on the way out — so `/tickets` stays the clean URL of the
 * default view.
 */

export const TICKET_SECTIONS = ['business', 'ads'] as const satisfies readonly TicketSection[]
export const DEFAULT_SECTION: TicketSection = 'business'

/**
 * The page's top-level tabs: the API's two sections, and Approvals — the
 * Data Upload Approvals (T16), worked as a queue of their own rather than
 * mixed in with a section's review.
 */
export const PAGE_TABS = [...TICKET_SECTIONS, 'approvals'] as const
export type PageTab = (typeof PAGE_TABS)[number]

/** The ticket type the Approvals tab holds. */
export const APPROVAL_TYPE = 'T16'

/** The Approvals tab's two queues: what waits for a decision, and what was decided. */
export const APPROVAL_VIEWS = [
  'review_pending',
  'reviewed_all',
] as const satisfies readonly TicketView[]

export const TICKET_VIEWS = [
  'unassigned',
  'assigned',
  'review_pending',
  'reviewed_all',
  'auto_solved',
  'my_pending',
  'my_inprogress',
  'my_resolved',
  'my_reviewed',
  'raised_pending_inprogress',
  'raised_open_to_review',
  'raised_reviewed',
  'notifications',
] as const satisfies readonly TicketView[]

/**
 * Whose view of the list an administrator is previewing: a reviewer's
 * (`tickets:edit`) or a team member's. Absent is their own. Honoured only for
 * an administrator; it changes which queues and actions show, never what the
 * backend lets anyone see or do.
 */
export const VIEW_AS = ['reviewer', 'member'] as const
export type ViewAs = (typeof VIEW_AS)[number]

export const TICKET_STATUSES = [
  'PENDING',
  'INPROGRESS',
  'RESOLVED',
  'REVIEWED',
] as const satisfies readonly TicketStatus[]

export const TICKET_SORTS = [
  'created_at',
  'age',
  'number',
  'status',
  'type',
  'owner_name',
  'creator_name',
  'brand_name',
  'store_name',
  'marketplace_name',
  'percent_completion',
  'assigned_at',
  'completed_at',
  'num_items',
  'data_type',
] as const satisfies readonly TicketSort[]

/**
 * The created-date presets the old URLs carried — today, the last 1/3/7/30/90
 * days — read for a bookmark and turned into a day range on the way in.
 */
const LEGACY_CREATED_PRESETS = ['today', '1d', '3d', '7d', '30d', '90d', 'custom'] as const

/** VentaHub offered 50–500; the API pages at most 100 rows. */
export const PAGE_SIZES = [20, 50, 100] as const
/** VentaHub: "always default to 100 rows per page". */
export const DEFAULT_PAGE_SIZE = 100
export const DEFAULT_SORT: TicketSort = 'created_at'

const page = z.number().int().positive().optional().catch(undefined)
const strings = z.array(z.string()).optional().catch(undefined)
const ids = z.array(z.number().int().positive()).optional().catch(undefined)
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

/**
 * A column's number filter, as the table's funnel writes it: one condition
 * and a value, or a min–max pair. Read from the URL like every other filter.
 */
const numberFilter = z
  .union([
    z.object({ op: z.enum(NUMBER_OPS), value: z.number() }),
    z.object({ op: z.literal('between'), min: z.number(), max: z.number() }),
  ])
  .optional()
  .catch(undefined)

/** The columns whose filters narrow the page in the browser. */
export const PAGE_FILTER_COLUMNS = ['items', 'progress', 'age'] as const
export type PageFilterColumn = (typeof PAGE_FILTER_COLUMNS)[number]

/**
 * The scope and date filters under the names every data page's URL uses —
 * `lib/filters`' primary keys — so a link from the Ads pages reads the same.
 * Optional here, where the shared schema defaults to `[]`: a clean ticket
 * URL carries nothing, and the page fills the empties in for the bar.
 */
const primaryKeys = {
  warehouseIds: ids.describe('Only tickets for these warehouses, by id.'),
  marketplaceIds: ids.describe('Only tickets for these marketplaces, by id.'),
  storeIds: ids.describe('Only tickets for these stores, by id.'),
  brandIds: ids.describe('Only tickets for these brands, by id.'),
  dateFrom: day.describe('First created day, YYYY-MM-DD, a whole local day; all time when absent.'),
  dateTo: day.describe('Last created day, YYYY-MM-DD, inclusive, a whole local day.'),
  /** Part of an item's SKU or ASIN, any case. */
  sku: z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .describe("Part of an item's SKU or ASIN, any case."),
} satisfies Partial<Record<keyof PrimaryFilterValue, z.ZodTypeAny>>

/**
 * The names the first release wrote — `stores`, `brands`, a created preset
 * and a `from`/`to` pair. Still read, so a bookmark or a shared link keeps
 * working, and rewritten to the names above by `migrateLegacySearch`.
 */
const legacyKeys = {
  warehouses: ids.describe('Legacy name for warehouseIds; read once and rewritten.'),
  brands: ids.describe('Legacy name for brandIds; read once and rewritten.'),
  marketplaces: ids.describe('Legacy name for marketplaceIds; read once and rewritten.'),
  stores: ids.describe('Legacy name for storeIds; read once and rewritten.'),
  created: z
    .enum(LEGACY_CREATED_PRESETS)
    .optional()
    .catch(undefined)
    .describe('Legacy created-date preset, turned into dateFrom/dateTo counted back from today.'),
  from: day.describe('Legacy name for dateFrom; read once and rewritten.'),
  to: day.describe('Legacy name for dateTo; read once and rewritten.'),
}

const ticketsSearchShape = z.object({
  /** The top-level tab: Business tickets, Ads tickets, or Approvals. */
  section: z
    .enum(PAGE_TABS)
    .optional()
    .catch(undefined)
    .describe(
      'The top-level tab: Business tickets, Ads tickets or Approvals; business by default.',
    ),
  /** On Approvals: the upload open beside the queue, so it can be linked. */
  detail: z
    .string()
    .min(1)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('On Approvals: the id of the upload ticket open beside the queue.'),
  /** An administrator's preview of another role's list. */
  as: z
    .enum(VIEW_AS)
    .optional()
    .catch(undefined)
    .describe(
      "An administrator's preview of a reviewer's or a team member's list; their own when absent.",
    ),
  view: z
    .enum(TICKET_VIEWS)
    .optional()
    .catch(undefined)
    .describe(
      "The queue on screen; the role's landing queue (To review or Unassigned) when absent.",
    ),
  page: page.describe('The page of the queue; 1 by default.'),
  limit: z
    .union([z.literal(20), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Tickets per page; 100 by default.'),
  sort: z
    .enum(TICKET_SORTS)
    .optional()
    .catch(undefined)
    .describe('The field the queue is sorted by; created_at by default.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('The sort direction; desc by default.'),
  /** Free text: comma is OR, space is AND. */
  q: z
    .string()
    .optional()
    .catch(undefined)
    .describe(
      'Free-text search over tickets, ASINs and SKUs: a comma means OR, a space means AND.',
    ),
  types: strings.describe('Only these ticket types, by id (T1, T9…).'),
  statuses: z
    .array(z.enum(TICKET_STATUSES))
    .optional()
    .catch(undefined)
    .describe('Only tickets in these statuses.'),
  ...primaryKeys,
  ...legacyKeys,
  owners: strings.describe('Only tickets owned by these people, by principal id.'),
  creators: strings.describe('Only tickets raised by these people, by principal id.'),
  overdue: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe('true for only tickets past their ETA, false for only those within it.'),
  /** Column filters applied to the loaded page: # Items, Progress, Ticket age (days). */
  items: numberFilter.describe('A number filter on # Items, applied to the loaded page.'),
  progress: numberFilter.describe('A number filter on Progress (%), applied to the loaded page.'),
  age: numberFilter.describe('A number filter on ticket age in days, applied to the loaded page.'),
  /** Columns shown beyond the view's own set, by column id. */
  show: strings.describe("Columns shown beyond the queue's own set, by column id."),
  /** Columns of the view's own set that are hidden, by column id. */
  hide: strings.describe("Columns of the queue's own set that are hidden, by column id."),
})

type RawTicketsSearch = z.infer<typeof ticketsSearchShape>

const DAY_MS = 86_400_000

/**
 * VentaHub's "Created" choices, in its order. A preset is a day range counted
 * back from today — the rule `migrateLegacySearch` applies to old links.
 */
export const CREATED_PRESETS = ['today', '1d', '3d', '7d', '30d', '90d'] as const
export type CreatedPreset = (typeof CREATED_PRESETS)[number]

/** The day range a preset stands for, as of `today`. */
export function createdPresetRange(
  preset: CreatedPreset,
  today: string = isoDay(new Date()),
): { dateFrom: string; dateTo: string } {
  const days = preset === 'today' ? 0 : Number(preset.replace('d', ''))
  return { dateFrom: addDays(today, -days), dateTo: today }
}

/** Which "Created" choice a range is: a preset, "custom", or all time (`undefined`). */
export function createdPresetOf(
  search: Pick<TicketsSearch, 'dateFrom' | 'dateTo'>,
  today: string = isoDay(new Date()),
): CreatedPreset | 'custom' | undefined {
  if (!search.dateFrom && !search.dateTo) return undefined
  return (
    CREATED_PRESETS.find((preset) => {
      const range = createdPresetRange(preset, today)
      return range.dateFrom === search.dateFrom && range.dateTo === search.dateTo
    }) ?? 'custom'
  )
}

/**
 * The old names onto the new: a legacy id list fills its primary key when
 * that is empty, and a created preset or `from`/`to` pair becomes a day
 * range. The legacy keys come out `undefined`, so they are never written back.
 */
export function migrateLegacySearch(search: RawTicketsSearch): RawTicketsSearch {
  const { warehouses, brands, marketplaces, stores, created, from, to, ...rest } = search
  const next: RawTicketsSearch = { ...rest }
  if (!next.warehouseIds?.length && warehouses?.length) next.warehouseIds = warehouses
  if (!next.brandIds?.length && brands?.length) next.brandIds = brands
  if (!next.marketplaceIds?.length && marketplaces?.length) next.marketplaceIds = marketplaces
  if (!next.storeIds?.length && stores?.length) next.storeIds = stores
  if (!next.dateFrom && !next.dateTo) {
    if (from || to) {
      next.dateFrom = from
      next.dateTo = to
    } else if (created && created !== 'custom') {
      // A preset counted back from the day the link is opened.
      Object.assign(next, createdPresetRange(created))
    }
  }
  return next
}

export const ticketsSearchSchema = ticketsSearchShape.transform(migrateLegacySearch)

export type TicketsSearch = z.infer<typeof ticketsSearchSchema>

/** The filters that narrow the rows, as opposed to view, page and order. */
const FILTER_KEYS = [
  'q',
  'types',
  'statuses',
  'warehouseIds',
  'brandIds',
  'marketplaceIds',
  'storeIds',
  'sku',
  'owners',
  'creators',
  'dateFrom',
  'dateTo',
  'overdue',
  ...PAGE_FILTER_COLUMNS,
] as const satisfies readonly (keyof TicketsSearch)[]

/** The bar's value from the URL: every primary key, empty where unset. */
export function primaryFilters(search: TicketsSearch): PrimaryFilterValue {
  return {
    warehouseIds: search.warehouseIds ?? [],
    marketplaceIds: search.marketplaceIds ?? [],
    storeIds: search.storeIds ?? [],
    brandIds: search.brandIds ?? [],
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    sku: search.sku,
    // The API's `sku` matches ASINs too; the bar's ASIN field is not shown.
    asin: undefined,
  }
}

/** The bar's value back into the URL: an empty list is no filter at all. */
export function fromPrimaryFilters(value: PrimaryFilterValue): Partial<TicketsSearch> {
  const list = (ids: number[]) => (ids.length ? ids : undefined)
  return {
    warehouseIds: list(value.warehouseIds),
    marketplaceIds: list(value.marketplaceIds),
    storeIds: list(value.storeIds),
    brandIds: list(value.brandIds),
    dateFrom: value.dateFrom,
    dateTo: value.dateTo,
    sku: value.sku?.trim() ? value.sku : undefined,
  }
}

/** The primary keys the tickets URL carries — every one but the unused ASIN. */
export const TICKET_PRIMARY_KEYS = PRIMARY_FILTER_KEYS.filter((key) => key !== 'asin')

export type TicketFilterKey = (typeof FILTER_KEYS)[number]

export function withoutTicketsSearchDefaults(search: TicketsSearch): TicketsSearch {
  const pruned: TicketsSearch = {}
  if (search.section && search.section !== DEFAULT_SECTION) pruned.section = search.section
  if (search.as) pruned.as = search.as
  if (search.detail) pruned.detail = search.detail
  // Kept whenever chosen: the landing queue depends on the role, which the
  // URL does not know, so an absent view is the only default there is.
  if (search.view) pruned.view = search.view
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  if (search.limit !== undefined && search.limit !== DEFAULT_PAGE_SIZE) pruned.limit = search.limit
  if (search.sort && search.sort !== DEFAULT_SORT) pruned.sort = search.sort
  if (search.order === 'asc') pruned.order = search.order
  if (search.q?.trim()) pruned.q = search.q
  for (const key of ['types', 'statuses', 'owners', 'creators', 'show', 'hide'] as const) {
    const values = search[key]
    if (values && values.length > 0) (pruned as Record<string, unknown>)[key] = values
  }
  if (search.sku?.trim()) pruned.sku = search.sku
  for (const key of ['warehouseIds', 'brandIds', 'marketplaceIds', 'storeIds'] as const) {
    const values = search[key]
    if (values && values.length > 0) pruned[key] = values
  }
  if (search.dateFrom) pruned.dateFrom = search.dateFrom
  if (search.dateTo) pruned.dateTo = search.dateTo
  if (search.overdue !== undefined) pruned.overdue = search.overdue
  for (const key of PAGE_FILTER_COLUMNS) {
    if (search[key]) pruned[key] = search[key]
  }
  return pruned
}

/** Whether any filter narrows the rows — what decides "no matches" vs "empty". */
export function hasFilters(search: TicketsSearch): boolean {
  return FILTER_KEYS.some((key) => {
    const value = search[key]
    return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== ''
  })
}

/**
 * Every filter cleared, ready to merge: what "Clear filters" sends. The
 * search box has its own clear, as VentaHub's did, so the query stays.
 */
export function clearedFilters(): Partial<TicketsSearch> {
  return Object.fromEntries(
    [...FILTER_KEYS.filter((key) => key !== 'q'), 'page'].map((key) => [key, undefined]),
  )
}

/**
 * The created-at bounds a search asks for, as ISO instants.
 *
 * Whole local days: `dateFrom` starts at its midnight and `dateTo` is
 * inclusive, so it ends at the next midnight. "The 7th" means the 7th where
 * the reader is, never a UTC instant that lands a day early west of it.
 */
export function createdRange(search: Pick<TicketsSearch, 'dateFrom' | 'dateTo'>): {
  created_from?: string
  created_to?: string
} {
  const range: { created_from?: string; created_to?: string } = {}
  if (search.dateFrom) range.created_from = fromIsoDay(search.dateFrom).toISOString()
  if (search.dateTo) {
    range.created_to = new Date(fromIsoDay(search.dateTo).getTime() + DAY_MS).toISOString()
  }
  return range
}

/** The filters as the API's counts take them. */
export function countParams(search: TicketsSearch, section: TicketSection): TicketCountParams {
  const params: TicketCountParams = { section, ...createdRange(search) }
  if (search.q?.trim()) params.q = search.q.trim()
  if (search.types?.length) params.ticket_type_ids = search.types
  if (search.statuses?.length) params.statuses = search.statuses
  if (search.warehouseIds?.length) params.warehouse_ids = search.warehouseIds
  if (search.brandIds?.length) params.brand_ids = search.brandIds
  if (search.marketplaceIds?.length) params.marketplace_ids = search.marketplaceIds
  if (search.storeIds?.length) params.store_ids = search.storeIds
  if (search.sku?.trim()) params.sku = search.sku.trim()
  if (search.owners?.length) params.owner_ids = search.owners
  if (search.creators?.length) params.creator_ids = search.creators
  if (search.overdue !== undefined) params.overdue = search.overdue
  return params
}

/** The page-side column filters a search carries, by column id. */
export function pageFilters(
  search: TicketsSearch,
): { id: PageFilterColumn; value: NumberFilter }[] {
  return PAGE_FILTER_COLUMNS.flatMap((id) => {
    const value = search[id]
    return value ? [{ id, value }] : []
  })
}

/** The URL's view state as the API's list query, for the queue on screen. */
export function listParams(
  search: TicketsSearch,
  section: TicketSection,
  view: TicketView,
): TicketListParams {
  return {
    ...countParams(search, section),
    view,
    page: search.page ?? 1,
    limit: search.limit ?? DEFAULT_PAGE_SIZE,
    sort: search.sort ?? DEFAULT_SORT,
    order: search.order ?? 'desc',
  }
}

// --- /tickets/$ticketId -------------------------------------------------------

export const ticketDetailSearchSchema = z.object({
  /** The page of the comment thread. */
  comments: page.describe('The page of the comment thread; 1 by default.'),
  /**
   * The list view the ticket was opened from, so Back and the breadcrumb
   * return to the same section, tab, filters and page. VentaHub stashed this
   * in sessionStorage; in the URL it survives a refresh and a shared link.
   */
  from: ticketsSearchSchema
    .optional()
    .catch(undefined)
    .describe('The tickets list view the ticket was opened from, for Back and the breadcrumb.'),
})

export type TicketDetailSearch = z.infer<typeof ticketDetailSearchSchema>

export function withoutTicketDetailSearchDefaults(search: TicketDetailSearch): TicketDetailSearch {
  const pruned: TicketDetailSearch = {}
  if (search.comments !== undefined && search.comments !== 1) pruned.comments = search.comments
  if (search.from) pruned.from = withoutTicketsSearchDefaults(search.from)
  return pruned
}

/** Where "back to the list" goes: where the user came from, else the ticket's section. */
export function listSearchFor(
  from: TicketsSearch | undefined,
  section: TicketSection | null,
): TicketsSearch {
  return withoutTicketsSearchDefaults(from ?? { section: section ?? DEFAULT_SECTION })
}

// --- /tickets/new ---------------------------------------------------------------

export const createTicketSearchSchema = z.object({
  /** The type whose form is open. */
  type: z
    .string()
    .regex(/^T\d{1,3}$/)
    .optional()
    .catch(undefined)
    .describe('The ticket type whose form is open, as T<n>; the type picker when absent.'),
})

export type CreateTicketSearch = z.infer<typeof createTicketSearchSchema>
